'use client';

import { useEffect } from 'react';

const OVERLAY_ID = 'pre-redirect-overlay';

const MESSENGER_TITLES = {
  telegram: 'Открываем Telegram...',
  whatsapp: 'Открываем WhatsApp...',
  max: 'Открываем Max...',
};

/**
 * Перехватывает клики по ссылкам на мессенджеры (Telegram / WhatsApp / Max),
 * показывает оверлей со слайдером «проведите вправо» (защита от ботов) и
 * после успешного свайпа отправляет цели в Яндекс.Метрику и делает редирект.
 *
 * Адаптировано из vanilla-скрипта под React:
 *  - ссылки помечаются data-атрибутами без подмены DOM-узлов (cloneNode/replaceChild
 *    ломает реконсиляцию React, особенно для ссылок внутри мобильного меню/диалогов);
 *  - реальный href сохраняется (graceful degradation, доступность), а навигацию
 *    перехватывает глобальный обработчик в фазе capture.
 */
export default function MessengerSliderRedirect({ ymId }) {
  const metrikaId =
    typeof ymId === 'string' ? ymId.trim() : String(ymId || '').trim();

  useEffect(() => {
    if (!metrikaId) {
      return undefined;
    }

    let lastTriggerTs = 0;

    function sendGoal(goal) {
      if (typeof window.ym === 'undefined') return;
      try {
        window.ym(metrikaId, 'reachGoal', goal);
      } catch (e) {
        /* noop */
      }
    }

    function getMessengerName(url) {
      if (!url) return null;
      const s = String(url).trim().toLowerCase();

      if (s.indexOf('tg://') === 0) return 'telegram';
      if (s.indexOf('whatsapp://') === 0) return 'whatsapp';
      if (s.indexOf('https://t.me') === 0 || s.indexOf('http://t.me') === 0) return 'telegram';
      if (s.indexOf('https://telegram.me') === 0 || s.indexOf('http://telegram.me') === 0) return 'telegram';
      if (s.indexOf('https://wa.me') === 0 || s.indexOf('http://wa.me') === 0) return 'whatsapp';
      if (s.indexOf('https://api.whatsapp.com') === 0 || s.indexOf('http://api.whatsapp.com') === 0) return 'whatsapp';
      if (s.indexOf('https://max.ru') === 0 || s.indexOf('http://max.ru') === 0) return 'max';

      try {
        const u = new URL(url, window.location.origin);
        const full = (u.hostname + u.pathname).toLowerCase();
        if (full.indexOf('t.me') !== -1 || full.indexOf('telegram.me') !== -1) return 'telegram';
        if (full.indexOf('wa.me') !== -1 || full.indexOf('api.whatsapp.com') !== -1) return 'whatsapp';
        if (full.indexOf('max.ru') !== -1) return 'max';
      } catch (e) {
        /* noop */
      }

      return null;
    }

    function hardStop(e) {
      if (!e) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      e.cancelBubble = true;
    }

    // React-safe: помечаем существующий узел data-атрибутами без подмены DOM.
    function patchLink(a) {
      if (!a || a.nodeType !== 1) return;
      if (a.getAttribute('data-msg-patched') === '1') return;

      const href = a.getAttribute('href');
      const name = getMessengerName(href);
      if (!name) return;

      a.setAttribute('data-msg-patched', '1');
      a.setAttribute('data-msg-name', name);
      a.setAttribute('data-msg-href', href);
    }

    function patchAll(root) {
      const scope = root && root.querySelectorAll ? root : document;
      if (scope.matches && scope.matches('a[href]')) patchLink(scope);

      const links = scope.querySelectorAll ? scope.querySelectorAll('a[href]') : [];
      for (let i = 0; i < links.length; i++) patchLink(links[i]);
    }

    function closeOverlay() {
      const el = document.getElementById(OVERLAY_ID);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }

    function showSliderAndRedirect(url, messengerName) {
      if (document.getElementById(OVERLAY_ID)) return;

      const title = MESSENGER_TITLES[messengerName] || 'Открываем мессенджер...';

      const overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.style.cssText =
        'position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:2147483647;display:flex;align-items:center;justify-content:center;cursor:pointer;';

      const box = document.createElement('div');
      box.style.cssText =
        'background:#fff;border-radius:16px;padding:28px 24px;max-width:90%;width:340px;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,.25);font-family:sans-serif;position:relative;cursor:default;z-index:2147483647;';
      box.innerHTML =
        '<button id="msg-close" type="button" aria-label="Закрыть" style="position:absolute;top:10px;right:10px;width:28px;height:28px;min-width:28px;min-height:28px;max-width:28px;max-height:28px;aspect-ratio:1/1;padding:0;border:none;border-radius:50%;box-sizing:border-box;appearance:none;-webkit-appearance:none;background:#f1f5f9;color:#4a5568;font-size:18px;line-height:28px;text-align:center;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:0 0 28px;">×</button>' +
        '<div style="font-size:20px;font-weight:700;margin-bottom:12px;">' + title + '</div>' +
        '<div style="font-size:14px;color:#333;margin-bottom:16px;line-height:1.45;">Проведите ползунок вправо, чтобы перейти</div>' +
        '<div id="msg-slider" style="width:100%;height:50px;background:#e0e0e0;border-radius:25px;position:relative;margin-bottom:12px;overflow:hidden;touch-action:none;">' +
          '<div id="msg-track" style="position:absolute;top:0;left:0;height:100%;width:100%;"></div>' +
          '<div id="msg-handle" style="position:absolute;top:5px;left:5px;width:40px;height:40px;background:#4285f4;border-radius:50%;cursor:grab;touch-action:none;display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;user-select:none;">→</div>' +
          '<div id="msg-slider-text" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#777;pointer-events:none;font-size:14px;">Проведите вправо</div>' +
        '</div>' +
        '<div style="font-size:12px;color:rgba(0,0,0,.35);margin-top:6px;">Защита от ботов</div>';

      overlay.appendChild(box);
      document.body.appendChild(overlay);

      const slider = box.querySelector('#msg-slider');
      const handle = box.querySelector('#msg-handle');
      const track = box.querySelector('#msg-track');
      const closeBtn = box.querySelector('#msg-close');

      let dragging = false;
      let startX = 0;
      let startLeft = 5;
      let maxLeft = 0;

      requestAnimationFrame(function () {
        maxLeft = slider.offsetWidth - handle.offsetWidth - 10;
      });

      function getX(e) {
        if (e.touches && e.touches[0]) return e.touches[0].clientX;
        if (e.changedTouches && e.changedTouches[0]) return e.changedTouches[0].clientX;
        return e.clientX;
      }

      function onStart(e) {
        dragging = true;
        startX = getX(e);
        startLeft = parseInt(handle.style.left, 10) || 5;
        handle.style.cursor = 'grabbing';
        handle.style.background = '#3367d6';
        hardStop(e);
      }

      function onMove(e) {
        if (!dragging) return;
        const x = getX(e);
        const next = Math.max(5, Math.min(startLeft + (x - startX), maxLeft));
        handle.style.left = next + 'px';
        const progress = maxLeft > 0 ? (next - 5) / maxLeft : 0;
        track.style.background =
          'linear-gradient(to right,#a0c3ff ' + (progress * 100) + '%,#e0e0e0 ' + (progress * 100) + '%)';
        hardStop(e);
      }

      function reset() {
        handle.style.transition = 'left .25s ease';
        handle.style.left = '5px';
        track.style.background = '#e0e0e0';
        setTimeout(function () {
          handle.style.transition = '';
        }, 250);
      }

      function cleanupOverlay() {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchend', onEnd);
        handle.removeEventListener('mousedown', onStart);
        handle.removeEventListener('touchstart', onStart);
        if (closeBtn) {
          closeBtn.removeEventListener('mousedown', onClosePress);
          closeBtn.removeEventListener('touchstart', onClosePress);
        }
        window.removeEventListener('pageshow', onPageShow);
      }

      function onPageShow() {
        closeOverlay();
        window.removeEventListener('pageshow', onPageShow);
      }

      function onClosePress(e) {
        cleanupOverlay();
        closeOverlay();
        hardStop(e);
      }

      function onEnd(e) {
        if (!dragging) return;
        dragging = false;
        handle.style.cursor = 'grab';
        handle.style.background = '#4285f4';

        const pos = parseInt(handle.style.left, 10) || 5;
        const success = pos >= maxLeft - 2;

        if (success) {
          sendGoal('messenger_slider_success');
          sendGoal('messenger_slider_success_' + messengerName);
          sendGoal('messenger_redirect_' + messengerName);

          cleanupOverlay();
          closeOverlay();

          try {
            window.location.href = url;
          } catch (err) {
            window.open(url, '_self');
          }
        } else {
          reset();
        }
        hardStop(e);
      }

      overlay.addEventListener(
        'click',
        function (e) {
          if (e.target === overlay) {
            cleanupOverlay();
            closeOverlay();
          }
          hardStop(e);
        },
        true,
      );

      handle.addEventListener('mousedown', onStart);
      handle.addEventListener('touchstart', onStart, { passive: false });
      if (closeBtn) closeBtn.addEventListener('mousedown', onClosePress);
      if (closeBtn) closeBtn.addEventListener('touchstart', onClosePress, { passive: false });
      document.addEventListener('mousemove', onMove, { passive: false });
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('mouseup', onEnd, { passive: false });
      document.addEventListener('touchend', onEnd, { passive: false });
      window.addEventListener('pageshow', onPageShow);
    }

    function findPatchedLink(target) {
      if (!target || !target.closest) return null;
      return target.closest("a[data-msg-patched='1'][data-msg-href]");
    }

    function globalBlocker(e) {
      const link = findPatchedLink(e.target);
      if (!link) return;

      hardStop(e);

      const type = e.type;
      if (type !== 'click' && type !== 'touchend' && type !== 'pointerup') return;

      const now = Date.now();
      if (now - lastTriggerTs < 400) return;
      lastTriggerTs = now;

      const url = link.getAttribute('data-msg-href');
      const name = link.getAttribute('data-msg-name') || getMessengerName(url);
      if (!url || !name) return;

      showSliderAndRedirect(url, name);
    }

    patchAll(document);

    const mo = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        for (let j = 0; j < m.addedNodes.length; j++) {
          const node = m.addedNodes[j];
          if (node && node.nodeType === 1) patchAll(node);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    const blockerEvents = [
      'pointerdown',
      'mousedown',
      'touchstart',
      'click',
      'auxclick',
      'touchend',
      'pointerup',
    ];
    blockerEvents.forEach(function (evt) {
      window.addEventListener(evt, globalBlocker, { capture: true, passive: false });
    });

    return () => {
      mo.disconnect();
      blockerEvents.forEach(function (evt) {
        window.removeEventListener(evt, globalBlocker, { capture: true });
      });
      closeOverlay();
    };
  }, [metrikaId]);

  return null;
}
